import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Play, Pause, Square, Clock } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';

interface TimerState {
  isRunning: boolean;
  seconds: number;
  projectId: string;
  taskId: string;
  description: string;
  startTime?: Date;
}

const ActiveTimer = () => {
  const [timer, setTimer] = useState<TimerState>({
    isRunning: false,
    seconds: 0,
    projectId: '',
    taskId: '',
    description: '',
  });

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
    if (timer.isRunning) {
      interval = setInterval(() => {
        setTimer((prev) => ({ ...prev, seconds: prev.seconds + 1 }));
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timer.isRunning]);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStart = () => {
    if (!timer.projectId) {
      toast.error('Please select a project');
      return;
    }
    setTimer((prev) => ({
      ...prev,
      isRunning: true,
      startTime: new Date(),
    }));
    toast.success('Timer started');
  };

  const handlePause = () => {
    setTimer((prev) => ({ ...prev, isRunning: false }));
    toast.info('Timer paused');
  };

  const handleStop = async () => {
    if (timer.seconds === 0) {
      toast.error('No time tracked');
      return;
    }

    // Mock API call to save time log
    console.log('Saving time log:', {
      projectId: timer.projectId,
      taskId: timer.taskId,
      description: timer.description,
      duration: timer.seconds,
      startTime: timer.startTime,
      endTime: new Date(),
    });

    await new Promise((resolve) => setTimeout(resolve, 500));
    toast.success(`Time log saved: ${formatTime(timer.seconds)}`);

    // Reset timer
    setTimer({
      isRunning: false,
      seconds: 0,
      projectId: '',
      taskId: '',
      description: '',
    });
  };

  return (
    <Card className={timer.isRunning ? 'border-green-500 border-2' : ''}>
      <CardContent className="p-6">
        <div className="space-y-4">
          {/* Timer Display */}
          <div className="flex items-center justify-center">
            <div className="flex items-center gap-3">
              <Clock className={`h-8 w-8 ${timer.isRunning ? 'text-green-500 animate-pulse' : 'text-muted-foreground'}`} />
              <div className="text-5xl font-bold font-mono">{formatTime(timer.seconds)}</div>
            </div>
          </div>

          {/* Timer Controls Form */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Project Select */}
            <Select
              value={timer.projectId}
              onValueChange={(value) => setTimer((prev) => ({ ...prev, projectId: value }))}
              disabled={timer.isRunning}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">LWM PM System</SelectItem>
                <SelectItem value="2">Barcelona E-commerce</SelectItem>
                <SelectItem value="3">Fixaro Platform</SelectItem>
              </SelectContent>
            </Select>

            {/* Task Select */}
            <Select
              value={timer.taskId}
              onValueChange={(value) => setTimer((prev) => ({ ...prev, taskId: value }))}
              disabled={timer.isRunning}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select task (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Setup authentication</SelectItem>
                <SelectItem value="2">Design dashboard</SelectItem>
                <SelectItem value="3">Fix payment gateway</SelectItem>
                <SelectItem value="4">Write API docs</SelectItem>
                <SelectItem value="5">Implement search</SelectItem>
              </SelectContent>
            </Select>

            {/* Description Input */}
            <Input
              placeholder="What are you working on?"
              value={timer.description}
              onChange={(e) => setTimer((prev) => ({ ...prev, description: e.target.value }))}
              disabled={timer.isRunning}
            />
          </div>

          {/* Control Buttons */}
          <div className="flex items-center justify-center gap-3">
            {!timer.isRunning ? (
              <Button onClick={handleStart} size="lg" className="min-w-[120px]">
                <Play className="h-4 w-4 mr-2" />
                Start
              </Button>
            ) : (
              <>
                <Button onClick={handlePause} variant="outline" size="lg" className="min-w-[120px]">
                  <Pause className="h-4 w-4 mr-2" />
                  Pause
                </Button>
                <Button onClick={handleStop} variant="destructive" size="lg" className="min-w-[120px]">
                  <Square className="h-4 w-4 mr-2" />
                  Stop & Save
                </Button>
              </>
            )}
          </div>

          {/* Current Task Info */}
          {timer.isRunning && timer.projectId && (
            <div className="text-center text-sm text-muted-foreground">
              <p>
                Working on: <span className="font-semibold">Project #{timer.projectId}</span>
                {timer.taskId && <span> - Task #{timer.taskId}</span>}
              </p>
              {timer.description && <p className="mt-1 italic">{timer.description}</p>}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ActiveTimer;
